import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.status === 201) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: ''
        });

        // Reset success message after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit form. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5F5F5] via-white to-[#F5F5F5]"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#227AFF]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6">
              Let's <span className="text-gradient">Connect</span>
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Have a question or ready to explore how automation can help your business?
            </p>
            <p className="text-lg text-gray-500">
              Reach out below or book a strategy call to get started.
            </p>
          </div>
        </div>
      </section>

      {/* Strategy Call CTA Banner */}
      <section className="py-8 bg-gradient-to-r from-[#227AFF] to-[#0A1538]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-white text-center md:text-left">
              <h3 className="text-xl font-semibold mb-2">Ready to identify what's costing you leads and revenue?</h3>
              <p className="text-white/80">Book a strategy call and get clarity on your next steps.</p>
            </div>
            <Link to="/book-consultation" className="btn bg-white text-[#227AFF] hover:bg-gray-100 whitespace-nowrap flex items-center gap-2">
              Book a Strategy Call <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div>
                <h2 className="mb-6">Get In Touch</h2>
                <p className="text-lg text-gray-600 mb-8">
                  Have a question before booking a call? Fill out the form and we'll get back to you within 24 hours.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 rounded-xl bg-[#227AFF]/10">
                      <Mail className="h-6 w-6 text-[#227AFF]" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Email</h4>
                      <a href="mailto:info@pecaroconsulting.com" className="text-gray-600 hover:text-[#227AFF] transition-colors">
                        info@pecaroconsulting.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="p-3 rounded-xl bg-[#227AFF]/10">
                      <Phone className="h-6 w-6 text-[#227AFF]" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Phone</h4>
                      <a href="tel:1-877-331-8445" className="text-gray-600 hover:text-[#227AFF] transition-colors">
                        1-877-331-8445
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="p-3 rounded-xl bg-[#227AFF]/10">
                      <MapPin className="h-6 w-6 text-[#227AFF]" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Service Area</h4>
                      <p className="text-gray-600">Serving businesses nationwide</p>
                    </div>
                  </div>
                </div>

                <div className="mt-10 bg-gradient-to-r from-[#227AFF]/10 to-[#007BFF]/10 rounded-2xl p-6">
                  <h4 className="font-semibold mb-3">What Happens Next?</h4>
                  <ol className="space-y-2">
                    <li className="flex items-start">
                      <span className="font-bold text-[#227AFF] mr-3">1.</span>
                      <span className="text-gray-700">We'll review your submission within 24 hours</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold text-[#227AFF] mr-3">2.</span>
                      <span className="text-gray-700">We'll reach out to understand your situation</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold text-[#227AFF] mr-3">3.</span>
                      <span className="text-gray-700">If there's a fit, we'll schedule a strategy call</span>
                    </li>
                  </ol>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-semibold mb-4">Thank You!</h3>
                    <p className="text-gray-600 mb-6">
                      We've received your message.
                    </p>
                    <p className="text-gray-600">
                      We'll be in touch within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <h3 className="text-2xl font-semibold mb-6">Send Us a Message</h3>
                    
                    {error && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    )}
                    
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#227AFF] focus:ring-2 focus:ring-[#227AFF]/20 outline-none transition-all"
                          placeholder="John Smith"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#227AFF] focus:ring-2 focus:ring-[#227AFF]/20 outline-none transition-all"
                          placeholder="john@company.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#227AFF] focus:ring-2 focus:ring-[#227AFF]/20 outline-none transition-all"
                          placeholder="(555) 123-4567"
                        />
                      </div>

                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#227AFF] focus:ring-2 focus:ring-[#227AFF]/20 outline-none transition-all"
                          placeholder="Your Company Inc."
                        />
                      </div>

                      <div>
                        <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                          What Are You Interested In?
                        </label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#227AFF] focus:ring-2 focus:ring-[#227AFF]/20 outline-none transition-all"
                        >
                          <option value="">Select an option</option>
                          <option value="altam-demo">Book Altam Demo</option>
                          <option value="system-audit">AI & Customer Experience System Audit</option>
                          <option value="phone-attendant">Automated Phone Attendant</option>
                          <option value="general">General Inquiry</option>
                          <option value="other">Other / Not Sure</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                          Your Message *
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows="4"
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#227AFF] focus:ring-2 focus:ring-[#227AFF]/20 outline-none transition-all resize-none"
                          placeholder="Tell us about your situation — what challenges are you facing? What prompted you to reach out?"
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full btn btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                        {!isSubmitting && <Send className="h-5 w-5" />}
                      </button>
                    </div>

                    <p className="text-xs text-gray-500 text-center mt-4">
                      By submitting this form, you agree to be contacted by Pecaro Consulting regarding your inquiry.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="mb-6 text-white">Your business deserves systems that work as hard as you do.</h2>
            <p className="text-xl text-gray-300 mb-8">
              Built by a senior project and systems strategist with 15+ years of real-world implementation experience.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mb-10">
              <div>
                <div className="text-4xl font-bold text-[#227AFF] mb-2">15+</div>
                <div className="text-gray-300">Years Experience</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[#227AFF] mb-2">Systems</div>
                <div className="text-gray-300">Not Just Advice</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[#227AFF] mb-2">Results</div>
                <div className="text-gray-300">That You Can Measure</div>
              </div>
            </div>
            <Link to="/book-consultation" className="btn btn-accent text-lg px-8">
              Book a Strategy Call
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
